/**
 * A check run on letter pages (Print.dc.html `check`, Oct 14): one page per check — who it pays
 * and how much, the amount in words, then the bills it pays line by line, so nobody phones to
 * ask. No signature image anywhere, on purpose: two directors sign by hand.
 */
import type { ReactNode } from 'react';
import { dayOf, Paper, PrintRoot, formatCalendarDate, formatMoney } from '@cider/ui';
import type { Bill, Check, Credit } from '../views/money/shared.js';
import { useLoad } from '../views/money/shared.js';

const ONES = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function words(n: number): string {
  if (n < 20) return ONES[n] ?? '';
  if (n < 100) return `${TENS[Math.floor(n / 10)] ?? ''}${n % 10 ? `-${ONES[n % 10] ?? ''}` : ''}`;
  if (n < 1000)
    return `${ONES[Math.floor(n / 100)] ?? ''} hundred${n % 100 ? ` ${words(n % 100)}` : ''}`;
  if (n < 1_000_000)
    return `${words(Math.floor(n / 1000))} thousand${n % 1000 ? ` ${words(n % 1000)}` : ''}`;
  return `${words(Math.floor(n / 1_000_000))} million${n % 1_000_000 ? ` ${words(n % 1_000_000)}` : ''}`;
}

/** "One thousand four hundred eight and 80/100" — the line on the check. */
export function amountInWords(cents: number): string {
  const dollars = Math.floor(cents / 100);
  const w = dollars === 0 ? 'zero' : words(dollars);
  return `${w.charAt(0).toUpperCase()}${w.slice(1)} and ${String(cents % 100).padStart(2, '0')}/100`;
}

export function CheckPages({ runId }: { runId: string }): ReactNode {
  const { data, error } = useLoad<{
    checks: (Check & { bills: Bill[]; credits: Credit[] })[];
    bank: { name: string; last4: string | null } | null;
  }>(`/money/checks/runs/${runId}`);
  if (error) return <p role="alert">{error}</p>;
  if (!data) return <p>Opening the run…</p>;
  const today = dayOf();
  return (
    <PrintRoot
      kind="letter"
      title={`Check run · ${String(data.checks.length)} ${data.checks.length === 1 ? 'check' : 'checks'}${data.checks.length ? ` · ${String(data.checks[0]?.number ?? '')}–${String(data.checks[data.checks.length - 1]?.number ?? '')}` : ''}`}
      pages={data.checks.map((c) => (
        <div key={c.number} data-check={c.number} className="check-page">
          <Paper
            size="check"
            masthead="Saint Lawrence Apple Fest"
            submast={`44633 Utica Rd, Utica MI 48317 · ${formatCalendarDate(today)}`}
            stamp={`CHECK ${String(c.number)}`}
            rows={[
              { key: 'pay', a: 'Pay to', b: c.payee, c: formatMoney(c.cents ?? 0) },
              { key: 'words', a: `${amountInWords(c.cents ?? 0)} dollars` },
              ...c.bills.map((b) => ({
                key: b.id,
                a: `${b.invoiceNumber ? `Invoice ${b.invoiceNumber} · ` : ''}${b.what}`,
                b: formatCalendarDate(b.billDate),
                c: formatMoney(b.totalCents),
              })),
              ...c.credits.map((cr) => ({
                key: cr.id,
                a: `Credit · ${cr.reason}`,
                c: formatMoney(-cr.cents),
              })),
              {
                key: 'bank',
                a: 'Drawn on',
                b: data.bank
                  ? `${data.bank.name}${data.bank.last4 ? ` · ${data.bank.last4}` : ''}`
                  : 'the operating account — Year settings names no bank',
              },
              {
                key: 'memo',
                a: 'Memo',
                b: c.bills.map((b) => b.invoiceNumber ?? b.what).join(', '),
              },
              { key: 'sign1', a: 'Signature', b: 'a director, by hand', c: '____________' },
              { key: 'sign2', a: 'Signature', b: 'a second director, by hand', c: '____________' },
            ]}
            foot="Numbers run in order with no gaps. A printed check is never deleted — it is voided, with a reason, and the number is never used again."
          />
        </div>
      ))}
    />
  );
}
